import PropTypes from 'prop-types';
import { useEffect } from 'react';
import {
  Link as RouterLink,
  useLocation,
  generatePath,
} from 'react-router';
// material
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Link,
  Stack,
  Drawer,
  Tooltip,
  Typography,
  CardActionArea,
  List,
} from '@mui/material';
// hooks
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
// routes
// components
import Logo from 'src/minimals/Logo';
import MyAvatar from 'src/minimals/MyAvatar';
import Scrollbar from 'src/minimals/Scrollbar';
import NavSection, { NavItem } from 'src/minimals/NavSection';
import { MHidden } from 'src/minimals/@material-extend';
//
import { useSelector } from 'react-redux';
import { currentWorkspaceSelectors } from 'src/selectors';
import { PATH_PAGE, PATH_WORKSPACE } from 'src/routes/paths';
import i18n from 'src/i18n';
import Settings from '@mui/icons-material/Settings';
import SidebarConfig from './SidebarConfig';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    // transition: theme.transitions.create('width', {
    //   duration: theme.transitions.duration.complex,
    // }),
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12],
}));

// ----------------------------------------------------------------------

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool,
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <CardActionArea
      onClick={onToggleCollapse}
      sx={{
        width: 24,
        height: 24,
        display: 'flex',
        cursor: 'pointer',
        borderRadius: '50%',
        alignItems: 'center',
        // color: 'text.primary',
        justifyContent: 'center',
        border: 'solid 1px',
        ...(collapseClick && {
          borderWidth: 2,
        }),
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: 'currentColor',
          // transition: (theme) => theme.transitions.create('all'),
          // ...(collapseClick && {
          //   width: 0,
          //   height: 0,
          // }),
        }}
      />
    </CardActionArea>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const workspace = useSelector(currentWorkspaceSelectors.getCurrentWorkspace);

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  } = useCollapseDrawer();
  const sidebarConfig = SidebarConfig({ workspaceId: workspace.id });

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center',
          }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {isCollapse ? (
              <Logo />
            ) : (
              <Logo logoSrc={'/logo_main_white_nobg.png'} width={140} />
            )}
          </Box>

          <MHidden width="lgDown">
            {!isCollapse && (
              <IconCollapse
                onToggleCollapse={onToggleCollapse}
                collapseClick={collapseClick}
              />
            )}
          </MHidden>
        </Stack>

        {isCollapse ? (
          <MyAvatar
            name={workspace?.name}
            sx={{ mx: 'auto', mb: 2 }}
            avatarSource={workspace?.logo}
          />
        ) : (
          <Link
            underline="none"
            component={RouterLink}
            to={PATH_PAGE.workspaces}
          >
            <AccountStyle>
              <MyAvatar name={workspace?.name} avatarSource={workspace?.logo} />
              <Box
                sx={{
                  ml: 2,
                  color: 'primary.contrastText',
                  overflowWrap: 'break-word',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    maxWidth: `calc(${DRAWER_WIDTH}px - 130px)`,
                  }}
                >
                  {workspace?.name}
                </Typography>
                <Typography variant="body2">{workspace?.role}</Typography>
              </Box>
            </AccountStyle>
          </Link>
        )}
      </Stack>

      <NavSection navConfig={sidebarConfig} isShow={!isCollapse} />

      <Box sx={{ flexGrow: 1 }} />
      <List disablePadding>
        <NavItem
          hasDarkBackground
          item={{
            title: i18n.t('sidebar.settings_tab_title'),
            path: generatePath(PATH_WORKSPACE.settings.root, {
              workspaceId: workspace.id,
            }),
            icon: <Settings />,
          }}
          isShow={!isCollapse}
        />
      </List>

      {!isCollapse && (
        <Stack
          alignItems="center"
          sx={{
            px: 5,
            pb: 5,
            mt: 10,
            width: 1,
            textAlign: 'center',
          }}
        >
          {/* <img
            alt="treinco_dashboard_illustration"
            src='/images/treinco_dashboard_illustration.webp'
          /> */}
        </Stack>
      )}
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              backgroundColor: 'primary.darker',
            },
          }}
          transitionDuration={0}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              backgroundColor: 'primary.darker',
              color: '#fff',

              ...(isCollapse && {
                width: COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                borderRight: 0,
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.88),
              }),
            },
          }}
          transitionDuration={0}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
